import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SettingsIcon from "@mui/icons-material/Settings";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Link, useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import {
  exportWorkInProgressCsv,
  fetchWorkInProgressReport,
  fetchWorkInProgressReportReps,
  searchReportCustomers,
} from "../../../../services/reports";
import { fetchTeams } from "../../../../services/teams";
import ReportRepAsyncSelect from "../shared/ReportRepAsyncSelect";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSummaryCard from "../shared/ReportSummaryCard";
import { normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import { UITableShell, UITableToolbar, UIStatusChip } from "../../../../components/ui";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  reportContentSx,
  reportFilterCardContentSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportSummaryGridSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  dateType: "createdDate",
  client: "",
  clientId: "",
  repId: "",
  assignmentFilter: "anyAssignment",
  teamId: "",
  stage: "all",
  invoiceStatus: "all",
  dueState: "all",
  hasBlocker: "all",
  quickFind: "",
};

const DEFAULT_SUMMARY = {
  totalOpenJobs: 0,
  totalBookedRevenue: 0,
  totalBookedCost: 0,
  totalBookedMargin: 0,
  overdueCount: 0,
  awaitingInvoiceCount: 0,
  blockedCount: 0,
};

const DEFAULT_OPTIONS = {
  stages: [],
  invoiceStatuses: [
    { value: "all", label: "All" },
    { value: "notInvoiced", label: "Not Invoiced" },
    { value: "partiallyInvoiced", label: "Partially Invoiced" },
    { value: "invoiced", label: "Invoiced" },
  ],
  dueStates: [
    { value: "all", label: "All" },
    { value: "overdue", label: "Overdue" },
    { value: "dueSoon", label: "Due Soon" },
    { value: "onTrack", label: "On Track" },
    { value: "noDate", label: "No Date" },
  ],
};

const ASSIGNMENT_FILTER_OPTIONS = [
  { value: "anyAssignment", label: "Any Assignment" },
  { value: "clientRep", label: "Client Rep Assignment" },
  { value: "orderRep", label: "Order Rep Assignment" },
];

const densitySettings = {
  compact: { py: 0.75, fontSize: "0.8125rem" },
  comfortable: { py: 1.4, fontSize: "0.875rem" },
  spacious: { py: 2.05, fontSize: "0.9375rem" },
};

const currency = (value) => {
  if (value == null || value === "") return "-";
  return `CA$${(Number(value) || 0).toFixed(2)}`;
};

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const percent = (value) => {
  if (value == null || value === "") return "-";
  return `${safeNumber(value).toFixed(1)}%`;
};

const ordinal = (day) => {
  const mod10 = day % 10;
  const mod100 = day % 100;
  if (mod10 === 1 && mod100 !== 11) return "st";
  if (mod10 === 2 && mod100 !== 12) return "nd";
  if (mod10 === 3 && mod100 !== 13) return "rd";
  return "th";
};

const friendlyDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const month = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}${ordinal(day)}, ${year}`;
};

const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      return imgSrc;
    }
  }
  return imgSrc;
};

const repDisplayName = (rep) => {
  const name = [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || rep?.email || (rep?.id != null ? `User ${rep.id}` : "");
};

const repInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatRepNames = (reps, fallback = "-") => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return fallback;
  return list.map(repDisplayName).filter(Boolean).join(", ") || fallback;
};

const RepList = ({ reps, fallback = "-" }) => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return <span>{fallback}</span>;
  const names = formatRepNames(list, fallback);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={names}>
      {list.slice(0, 3).map((rep, index) => {
        const name = repDisplayName(rep);
        return (
          <Avatar
            key={`${rep?.id || rep?.email || name || "rep"}-${index}`}
            src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
            alt={name}
            sx={{ width: 26, height: 26, fontSize: 12 }}
          >
            {repInitials(name)}
          </Avatar>
        );
      })}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {names}
      </Box>
      {list.length > 3 ? <Chip size="small" variant="outlined" label={`+${list.length - 3}`} /> : null}
    </Box>
  );
};

const customerOptionLabel = (option) => option?.label || option?.businessName || option?.name || "";

const normalizeSavedCustomerOption = (customer) => {
  if (!customer) return null;
  const id = customer?.id != null && String(customer.id).trim() !== "" ? String(customer.id) : "";
  const label = customer?.label || customer?.businessName || customer?.name || customer?.customerName || customer?.emailAddress || (id ? `Customer #${id}` : "");
  if (!label && !id) return null;
  return {
    id: id || null,
    label,
    businessName: customer?.businessName || label,
    emailAddress: customer?.emailAddress || "",
  };
};

const normalizeSavedRepOption = (rep) => {
  if (!rep) return null;
  const id = rep?.id != null && String(rep.id).trim() !== "" ? String(rep.id) : "";
  const normalized = {
    id: id || null,
    first_name: rep?.first_name || rep?.firstName || "",
    last_name: rep?.last_name || rep?.lastName || "",
    firstName: rep?.firstName || rep?.first_name || "",
    lastName: rep?.lastName || rep?.last_name || "",
    email: rep?.email || "",
    profilePicture: rep?.profilePicture || null,
    avatar: rep?.avatar || null,
    photoUrl: rep?.photoUrl || null,
    role: rep?.role || "",
    roleLabel: rep?.roleLabel || "",
    label: rep?.label || "",
  };
  const fallbackLabel = repDisplayName(normalized);
  if (!normalized.id && !fallbackLabel) return null;
  normalized.label = normalized.label || fallbackLabel;
  return normalized;
};

const roleNormalized = (value = "") => String(value || "").replace(/_/g, "-").toLowerCase().trim();

const getStageTone = (value = "") => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("invoice")) return "warning";
  if (normalized.includes("ship")) return "accent";
  if (normalized.includes("quality")) return "info";
  if (normalized.includes("production")) return "success";
  if (normalized.includes("proof")) return "warning";
  return "neutral";
};

const getInvoiceTone = (value = "") => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("partial")) return "warning";
  if (normalized.includes("invoiced")) return "success";
  if (normalized.includes("not")) return "neutral";
  return "neutral";
};

const getDueTone = (value = "") => {
  if (value === "overdue") return "danger";
  if (value === "dueSoon") return "warning";
  if (value === "onTrack") return "success";
  return "neutral";
};

const getSortedRows = (rows, columns, sortBy, sortDir) => {
  const safeRows = Array.isArray(rows) ? [...rows] : [];
  if (!sortBy) return safeRows;
  const column = columns.find((item) => (item.field || item.headerName) === sortBy);
  if (!column) return safeRows;

  const readValue = (row) => {
    if (typeof column.sortValue === "function") return column.sortValue({ row, value: row?.[column.field] });
    return row?.[column.field];
  };

  const compareValues = (leftValue, rightValue) => {
    if (column.sortType === "number" || column.sortType === "currency" || column.sortType === "percent") {
      return safeNumber(leftValue, Number.NEGATIVE_INFINITY) - safeNumber(rightValue, Number.NEGATIVE_INFINITY);
    }
    if (column.sortType === "date") {
      const leftTime = leftValue ? new Date(leftValue).getTime() : Number.NEGATIVE_INFINITY;
      const rightTime = rightValue ? new Date(rightValue).getTime() : Number.NEGATIVE_INFINITY;
      return leftTime - rightTime;
    }
    return String(leftValue || "").localeCompare(String(rightValue || ""), undefined, { sensitivity: "base" });
  };

  safeRows.sort((left, right) => {
    const delta = compareValues(readValue(left), readValue(right));
    return sortDir === "desc" ? -delta : delta;
  });

  return safeRows;
};

function ReportCustomerAsyncSelect({
  instanceUniqueId,
  value,
  onChange,
  seedOptions = [],
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(Array.isArray(seedOptions) ? seedOptions : []);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    setInputValue(value ? customerOptionLabel(value) : "");
  }, [value]);

  useEffect(() => {
    setOptions((previous) => {
      const map = new Map(previous.map((option) => [String(option?.id || ""), option]));
      (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
        if (!option) return;
        map.set(String(option?.id || ""), option);
      });
      if (value?.id) map.set(String(value.id), value);
      return Array.from(map.values()).filter((option) => option && option.id != null);
    });
  }, [seedOptions, value]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      const requestId = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await searchReportCustomers({
          instanceUniqueId,
          q: inputValue,
          page: 1,
          limit: 25,
        });
        if (requestId !== requestSeq.current) return;
        const customers = Array.isArray(res?.customers) ? res.customers : [];
        setOptions((previous) => {
          const map = new Map();
          customers.forEach((customer) => {
            if (!customer?.id) return;
            map.set(String(customer.id), {
              id: customer.id,
              businessName: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
              emailAddress: customer.emailAddress || "",
              label: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
            });
          });
          (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
            if (!option?.id || map.has(String(option.id))) return;
            map.set(String(option.id), option);
          });
          if (value?.id && !map.has(String(value.id))) map.set(String(value.id), value);
          return Array.from(map.values());
        });
      } catch (error) {
        if (requestId !== requestSeq.current) return;
        console.error("Failed to search report customers", error);
      } finally {
        if (requestId === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [open, inputValue, instanceUniqueId, seedOptions, value]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "reset") {
          setInputValue(value ? customerOptionLabel(value) : "");
          return;
        }
        if (reason === "clear") {
          setInputValue("");
          return;
        }
        setInputValue(nextValue);
      }}
      onChange={(_, option) => {
        setInputValue(option ? customerOptionLabel(option) : "");
        onChange(option || null);
      }}
      isOptionEqualToValue={(option, currentValue) => String(option?.id || "") === String(currentValue?.id || "")}
      getOptionLabel={customerOptionLabel}
      renderOption={(props, option) => (
        <li {...props} key={option?.id || option?.label}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {customerOptionLabel(option)}
            </Typography>
            {option?.emailAddress ? (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {option.emailAddress}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Customer"
          placeholder="Search customers"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function ReportTableContent({
  rows = [],
  columns = [],
  loading = false,
  emptyMessage = "No data available.",
  page = 0,
  rowsPerPage = 25,
  sortBy = "",
  sortDir = "asc",
  onSort = () => {},
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  density = "comfortable",
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  const pagedRows = safeRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={0} sx={reportTablePaperSx}>
      <TableContainer sx={reportTableContainerSx}>
        <Table size="small" stickyHeader sx={{ minWidth: 1680 }}>
          <TableHead>
            <TableRow>
              {safeColumns.map((column) => {
                const columnKey = column.field || column.headerName;
                const active = sortBy === columnKey;
                const sortable = column.sortable !== false;
                return (
                  <TableCell
                    key={columnKey}
                    onClick={() => sortable && onSort(columnKey)}
                    sx={reportTableHeaderCellSx({
                      active,
                      sortable,
                      minWidth: column.minWidth || 130,
                    })}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{column.headerName}</span>
                      {sortable ? (
                        active ? (
                          sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                        )
                      ) : null}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody sx={reportTableBodySx}>
            {loading
              ? Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
                  <TableRow key={`loading-${rowIndex}`}>
                    {safeColumns.map((column) => (
                      <TableCell key={`${column.field || column.headerName}-${rowIndex}`} sx={{ px: 2, py: densitySettings[density].py }}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : pagedRows.map((row, rowIndex) => (
                  <TableRow key={row?.id || row?.orderId || rowIndex} hover>
                    {safeColumns.map((column) => {
                      const rendered = typeof column.renderCell === "function"
                        ? column.renderCell({ value: row?.[column.field], row })
                        : (row?.[column.field] ?? "-");
                      return (
                        <TableCell
                          key={column.field || column.headerName}
                          sx={{
                            py: densitySettings[density].py,
                            fontSize: densitySettings[density].fontSize,
                            verticalAlign: "top",
                            whiteSpace: column.wrap ? "normal" : "nowrap",
                          }}
                        >
                          {rendered}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            {!loading && !pagedRows.length ? (
              <TableRow>
                <TableCell colSpan={safeColumns.length || 1} sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {safeRows.length
            ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, safeRows.length)} of ${safeRows.length}`
            : "No results"}
        </Typography>
        <TablePagination
          component="div"
          count={safeRows.length}
          page={page}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            "& .MuiTablePagination-toolbar": { minHeight: 44, px: 0 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              mb: 0,
              fontSize: "0.8125rem",
            },
          }}
        />
      </Box>
    </Paper>
  );
}

export default function WorkInProgressReport() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("dueDate");
  const [sortDir, setSortDir] = useState("asc");
  const [density, setDensity] = useState("comfortable");
  const [densityMenuEl, setDensityMenuEl] = useState(null);
  const reportRequestSeq = useRef(0);
  const skipNextAutoLoadRef = useRef(false);

  const normalizedViewerRole = roleNormalized(authState?.role);
  const selfScopedViewer = normalizedViewerRole === "client-rep" || normalizedViewerRole === "order-rep";
  const forcedAssignmentFilter = normalizedViewerRole === "order-rep" ? "orderRep" : "clientRep";
  const repPickerDisabled = selfScopedViewer;
  const assignmentHelperText = "Assignment Filter controls which projects are included. Both Client Rep and Order Rep columns remain visible because they are project details.";

  useEffect(() => {
    if (selfScopedViewer) {
      setFilters((previous) => ({
        ...previous,
        assignmentFilter: forcedAssignmentFilter,
        repId: "",
      }));
    }
  }, [forcedAssignmentFilter, selfScopedViewer]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:workInProgress:columns");
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:workInProgress:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  useEffect(() => {
    let active = true;
    const loadTeams = async () => {
      setTeamsLoading(true);
      try {
        const res = await fetchTeams({ instanceUniqueId });
        if (!active) return;
        setTeamOptions(Array.isArray(res?.teams) ? res.teams : []);
      } catch (error) {
        if (active) {
          console.error("Failed to load WIP teams", error);
          setTeamOptions([]);
        }
      } finally {
        if (active) setTeamsLoading(false);
      }
    };
    loadTeams();
    return () => {
      active = false;
    };
  }, [instanceUniqueId]);

  const load = useCallback(async (filtersOverride = null) => {
    const requestId = ++reportRequestSeq.current;
    const effectiveFilters = filtersOverride && typeof filtersOverride === "object"
      ? filtersOverride
      : filters;
    setLoading(true);
    try {
      const params = {
        instanceUniqueId,
        ...effectiveFilters,
        assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : effectiveFilters.assignmentFilter,
      };
      const res = await fetchWorkInProgressReport(params);
      if (requestId !== reportRequestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(res?.summary && typeof res.summary === "object" ? { ...DEFAULT_SUMMARY, ...res.summary } : DEFAULT_SUMMARY);
      setOptions(res?.options && typeof res.options === "object" ? { ...DEFAULT_OPTIONS, ...res.options } : DEFAULT_OPTIONS);
      const nextCustomerOptions = Array.isArray(res?.customerOptions) ? res.customerOptions : [];
      setCustomerOptions(nextCustomerOptions);
      if (!effectiveFilters.clientId) {
        setSelectedCustomer(null);
      } else {
        const matched = nextCustomerOptions.find((option) => String(option?.id || "") === String(effectiveFilters.clientId));
        setSelectedCustomer((previous) => matched || previous || null);
      }
    } catch (error) {
      if (requestId !== reportRequestSeq.current) return;
      console.error("Failed to load work in progress report", {
        error,
        status: error?.response?.status,
        response: error?.response?.data,
      });
      setRows([]);
      setSummary(DEFAULT_SUMMARY);
      setOptions(DEFAULT_OPTIONS);
      setCustomerOptions([]);
    } finally {
      if (requestId === reportRequestSeq.current) setLoading(false);
    }
  }, [filters, forcedAssignmentFilter, instanceUniqueId, selfScopedViewer]);

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= rows.length) {
      setPage(0);
    }
  }, [page, rows.length, rowsPerPage]);

  const handleSort = useCallback((columnKey) => {
    if (sortBy === columnKey) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortBy("");
        setSortDir("asc");
      }
      return;
    }
    setSortBy(columnKey);
    setSortDir("asc");
  }, [sortBy, sortDir]);

  const columns = useMemo(() => ([
    {
      field: "orderNumber",
      headerName: "Project / Order #",
      minWidth: 160,
      sortType: "string",
      renderCell: ({ value }) => (
        <Link
          to={`/instance/${instanceUniqueId}/sales/orders/${value}/overview`}
          style={{ color: "#1976d2", textDecoration: "none", fontWeight: 600 }}
        >
          {value || "-"}
        </Link>
      ),
    },
    { field: "projectTitle", headerName: "Project", minWidth: 220, wrap: true, sortType: "string" },
    { field: "customerName", headerName: "Customer", minWidth: 220, wrap: true, sortType: "string" },
    {
      field: "currentStage",
      headerName: "Current WIP Stage",
      minWidth: 180,
      sortType: "string",
      renderCell: ({ value }) => <UIStatusChip label={value || "-"} tone={getStageTone(value)} />,
    },
    { field: "overallStatus", headerName: "Overall Status", minWidth: 180, sortType: "string" },
    {
      field: "clientRepDetails",
      headerName: "Client Rep",
      minWidth: 220,
      sortValue: ({ row }) => formatRepNames(row.clientRepDetails, row.clientRep),
      renderCell: ({ row }) => <RepList reps={row.clientRepDetails} fallback={row.clientRep || "-"} />,
    },
    {
      field: "orderRepDetails",
      headerName: "Order Rep",
      minWidth: 220,
      sortValue: ({ row }) => formatRepNames(row.orderRepDetails, row.orderRep),
      renderCell: ({ row }) => <RepList reps={row.orderRepDetails} fallback={row.orderRep || "-"} />,
    },
    { field: "createdAt", headerName: "Created Date", minWidth: 160, sortType: "date", renderCell: ({ value }) => friendlyDate(value) },
    { field: "dueDate", headerName: "In-Hands / Due Date", minWidth: 170, sortType: "date", renderCell: ({ value }) => friendlyDate(value) },
    { field: "daysOpen", headerName: "Days Open", minWidth: 110, sortType: "number" },
    {
      field: "daysUntilDue",
      headerName: "Days Until Due",
      minWidth: 130,
      sortType: "number",
      renderCell: ({ row, value }) => (
        row?.dueState === "noDate"
          ? "-"
          : <UIStatusChip label={value == null ? "-" : String(value)} tone={getDueTone(row?.dueState)} />
      ),
    },
    { field: "nextAction", headerName: "Next Action", minWidth: 220, wrap: true, sortType: "string" },
    {
      field: "invoiceStatusLabel",
      headerName: "Invoice Status",
      minWidth: 150,
      sortValue: ({ row }) => row.invoiceStatus,
      renderCell: ({ value }) => <UIStatusChip label={value || "-"} tone={getInvoiceTone(value)} />,
    },
    { field: "bookedRevenue", headerName: "Booked Revenue", minWidth: 140, sortType: "currency", renderCell: ({ value }) => currency(value) },
    { field: "bookedCost", headerName: "Booked Cost", minWidth: 130, sortType: "currency", renderCell: ({ value }) => currency(value) },
    { field: "bookedMarginAmount", headerName: "Booked Margin", minWidth: 140, sortType: "currency", renderCell: ({ value }) => currency(value) },
    { field: "invoicedTotal", headerName: "Invoiced Total", minWidth: 140, sortType: "currency", renderCell: ({ value }) => currency(value) },
    { field: "uninvoicedAmount", headerName: "Uninvoiced Amount", minWidth: 150, sortType: "currency", renderCell: ({ value }) => currency(value) },
    { field: "attentionReason", headerName: "Attention Reason", minWidth: 240, wrap: true, sortType: "string" },
    {
      field: "proofStatus",
      headerName: "Proof Status",
      minWidth: 150,
      sortType: "string",
    },
    {
      field: "purchasingStatus",
      headerName: "Purchasing Status",
      minWidth: 150,
      sortType: "string",
    },
    {
      field: "productionStatus",
      headerName: "Production Status",
      minWidth: 150,
      sortType: "string",
    },
    {
      field: "shippingStatus",
      headerName: "Shipping Status",
      minWidth: 140,
      sortType: "string",
    },
    {
      field: "hasBlocker",
      headerName: "Has Blocker",
      minWidth: 120,
      sortType: "string",
      renderCell: ({ value }) => <UIStatusChip label={value ? "Blocked" : "Clear"} tone={value ? "danger" : "success"} />,
    },
    { field: "lastActivityAt", headerName: "Last Activity", minWidth: 170, sortType: "date", renderCell: ({ value }) => friendlyDate(value) },
  ]), [instanceUniqueId]);

  useEffect(() => {
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));
    setHiddenColumns((previous) => {
      const next = previous.filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.includes(String(column.field || column.headerName || ""))),
    [columns, hiddenColumns],
  );

  useEffect(() => {
    if (!sortBy) return;
    const visibleSortColumn = visibleColumns.some((column) => (column.field || column.headerName) === sortBy);
    if (!visibleSortColumn) {
      setSortBy("");
      setSortDir("asc");
    }
  }, [sortBy, visibleColumns]);

  const sortedRows = useMemo(() => getSortedRows(rows, columns, sortBy, sortDir), [columns, rows, sortBy, sortDir]);

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      ...filters,
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : filters.assignmentFilter,
    },
    selectedCustomer: normalizeSavedCustomerOption(selectedCustomer),
    selectedRep: normalizeSavedRepOption(selectedRep),
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
    density,
    sortBy,
    sortDir,
    rowsPerPage,
  }), [density, filters, forcedAssignmentFilter, hiddenColumns, rowsPerPage, selectedCustomer, selectedRep, selfScopedViewer, sortBy, sortDir]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object" ? savedState.filters : {};
    const savedCustomer = normalizeSavedCustomerOption(savedState?.selectedCustomer)
      || normalizeSavedCustomerOption(savedFilters?.clientId ? { id: savedFilters.clientId, label: savedFilters.client || `Customer #${savedFilters.clientId}` } : null);
    const savedRep = normalizeSavedRepOption(savedState?.selectedRep)
      || normalizeSavedRepOption(savedFilters?.repId ? { id: savedFilters.repId, label: `User ${savedFilters.repId}` } : null);
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));

    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: [],
    });

    setSelectedCustomer(savedCustomer);
    setSelectedRep(savedRep);
    if (savedCustomer?.id) {
      setCustomerOptions((previous) => (
        previous.some((option) => String(option?.id || "") === String(savedCustomer.id))
          ? previous
          : [...previous, savedCustomer]
      ));
    }
    const nextFilters = {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      client: savedCustomer ? customerOptionLabel(savedCustomer) : String(savedFilters?.client || ""),
      clientId: savedCustomer?.id ? String(savedCustomer.id) : "",
      repId: selfScopedViewer ? "" : (savedRep?.id ? String(savedRep.id) : ""),
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : String(savedFilters?.assignmentFilter || DEFAULT_FILTERS.assignmentFilter),
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      dateType: String(savedFilters?.dateType || DEFAULT_FILTERS.dateType),
      teamId: String(savedFilters?.teamId || ""),
      stage: String(savedFilters?.stage || DEFAULT_FILTERS.stage),
      invoiceStatus: String(savedFilters?.invoiceStatus || DEFAULT_FILTERS.invoiceStatus),
      dueState: String(savedFilters?.dueState || DEFAULT_FILTERS.dueState),
      hasBlocker: String(savedFilters?.hasBlocker || DEFAULT_FILTERS.hasBlocker),
      quickFind: String(savedFilters?.quickFind || ""),
    };

    setHiddenColumns(nextHiddenColumns);
    setDensity(["compact", "comfortable", "spacious"].includes(savedState?.density) ? savedState.density : "comfortable");
    setSortBy(String(savedState?.sortBy || ""));
    setSortDir(savedState?.sortDir === "desc" ? "desc" : "asc");
    setRowsPerPage([10, 25, 50, 100].includes(Number(savedState?.rowsPerPage)) ? Number(savedState.rowsPerPage) : 25);
    setPage(0);
    skipNextAutoLoadRef.current = true;
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, forcedAssignmentFilter, load, selfScopedViewer]);

  const onExport = async () => {
    const response = await exportWorkInProgressCsv({
      instanceUniqueId,
      ...filters,
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : filters.assignmentFilter,
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "work-in-progress-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const summaryCards = [
    { key: "open-jobs", label: "Open WIP Jobs", value: summary.totalOpenJobs ?? 0, tone: "count" },
    { key: "booked-revenue", label: "Booked WIP Revenue", value: currency(summary.totalBookedRevenue), tone: "sales" },
    { key: "booked-cost", label: "Booked WIP Cost", value: currency(summary.totalBookedCost), tone: "warning" },
    { key: "booked-margin", label: "Booked WIP Margin", value: currency(summary.totalBookedMargin), tone: safeNumber(summary.totalBookedMargin) < 0 ? "danger" : "margin" },
    { key: "overdue-jobs", label: "Overdue Jobs", value: summary.overdueCount ?? 0, tone: (summary.overdueCount || 0) > 0 ? "danger" : "count" },
    { key: "awaiting-invoice", label: "Awaiting Invoice", value: summary.awaitingInvoiceCount ?? 0, tone: (summary.awaitingInvoiceCount || 0) > 0 ? "warning" : "count" },
  ];

  const formatTeamOptionLabel = (team) => {
    if (!team) return "All Teams";
    const memberCount = Number(team.memberCount ?? (Array.isArray(team.memberUserIds) ? team.memberUserIds.length : 0)) || 0;
    const memberLabel = `${memberCount} member${memberCount === 1 ? "" : "s"}`;
    return team.type ? `${team.name} - ${team.type} - ${memberLabel}` : `${team.name} - ${memberLabel}`;
  };

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    Work in Progress Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${Array.isArray(rows) ? rows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="workInProgress"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="workInProgress"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <IconButton
                      size="small"
                      onClick={(event) => setDensityMenuEl(event.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <SettingsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={densityMenuEl}
                    open={Boolean(densityMenuEl)}
                    onClose={() => setDensityMenuEl(null)}
                    slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
                  >
                    <Typography variant="overline" sx={{ px: 2, pt: 1.25, pb: 0.5, display: "block", color: "text.disabled" }}>
                      Row density
                    </Typography>
                    {["compact", "comfortable", "spacious"].map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => {
                          setDensity(option);
                          setDensityMenuEl(null);
                        }}
                        selected={density === option}
                      >
                        <Typography variant="body2" sx={{ textTransform: "capitalize", fontWeight: 500 }}>
                          {option}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={reportContentSx}>
              <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 2, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.08em" }}>
                      Report Filters
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Work in Progress Report Controls
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active approved jobs across proofing, purchasing, production, shipping, and invoicing preparation.
                    </Typography>
                  </Box>
                  <UIStatusChip label={`${summary.blockedCount || 0} blocked`} tone={(summary.blockedCount || 0) > 0 ? "warning" : "neutral"} />
                </Stack>

                <Box sx={{ ...reportFilterCardContentSx, ...reportFilterGridSx }}>
                  <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dateFrom: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dateTo: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    select
                    label="Date Type"
                    value={filters.dateType}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dateType: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="createdDate">Created Date</option>
                    <option value="inHandsDate">In-Hands Date</option>
                  </TextField>
                  <ReportCustomerAsyncSelect
                    instanceUniqueId={instanceUniqueId}
                    value={selectedCustomer}
                    seedOptions={customerOptions}
                    onChange={(customer) => {
                      setSelectedCustomer(customer);
                      setFilters((previous) => ({
                        ...previous,
                        clientId: customer?.id ? String(customer.id) : "",
                        client: customer ? customerOptionLabel(customer) : "",
                      }));
                    }}
                  />
                  {!selfScopedViewer ? (
                    <TextField
                      select
                      label="Assignment Type"
                      value={filters.assignmentFilter}
                      onChange={(event) => setFilters((previous) => ({ ...previous, assignmentFilter: event.target.value, repId: "" }))}
                      SelectProps={{ native: true }}
                      size="small"
                      helperText={assignmentHelperText}
                    >
                      {ASSIGNMENT_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </TextField>
                  ) : null}
                  {!repPickerDisabled ? (
                    <ReportRepAsyncSelect
                      instanceUniqueId={instanceUniqueId}
                      value={selectedRep}
                      searchFn={fetchWorkInProgressReportReps}
                      roles={["client-rep", "order-rep", "administrator", "admin"]}
                      seedOptions={selectedRep ? [selectedRep] : []}
                      label="Representative"
                      onChange={(rep) => {
                        setSelectedRep(rep);
                        setFilters((previous) => ({
                          ...previous,
                          repId: rep?.id ? String(rep.id) : "",
                        }));
                      }}
                    />
                  ) : null}
                  <TextField
                    select
                    label="Team"
                    value={filters.teamId}
                    onChange={(event) => setFilters((previous) => ({ ...previous, teamId: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                    disabled={teamsLoading}
                  >
                    <option value="">{teamsLoading ? "Loading teams..." : "All Teams"}</option>
                    {teamOptions.map((team) => (
                      <option key={team.id} value={team.id}>
                        {formatTeamOptionLabel(team)}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Current WIP Stage"
                    value={filters.stage}
                    onChange={(event) => setFilters((previous) => ({ ...previous, stage: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="all">All Stages</option>
                    {(Array.isArray(options.stages) ? options.stages : []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Invoice Status"
                    value={filters.invoiceStatus}
                    onChange={(event) => setFilters((previous) => ({ ...previous, invoiceStatus: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    {(Array.isArray(options.invoiceStatuses) ? options.invoiceStatuses : []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Due State"
                    value={filters.dueState}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dueState: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    {(Array.isArray(options.dueStates) ? options.dueStates : []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Has Blocker"
                    value={filters.hasBlocker}
                    onChange={(event) => setFilters((previous) => ({ ...previous, hasBlocker: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="all">All</option>
                    <option value="blocked">Blocked</option>
                    <option value="notBlocked">Not Blocked</option>
                  </TextField>
                  <TextField
                    label="Quick Find"
                    value={filters.quickFind}
                    onChange={(event) => setFilters((previous) => ({ ...previous, quickFind: event.target.value }))}
                    size="small"
                  />
                </Box>
              </Paper>

              <Box sx={reportSummaryGridSx}>
                {summaryCards.map((card) => (
                  <ReportSummaryCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    tone={card.tone}
                  />
                ))}
              </Box>

              <Box sx={reportTableSectionSx}>
                <ReportTableContent
                  rows={sortedRows}
                  columns={visibleColumns}
                  loading={loading}
                  emptyMessage="No work in progress items found for the selected filters."
                  page={page}
                  rowsPerPage={rowsPerPage}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  onPageChange={setPage}
                  onRowsPerPageChange={(nextRowsPerPage) => {
                    setRowsPerPage(nextRowsPerPage);
                    setPage(0);
                  }}
                  density={density}
                />
              </Box>
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
}
